// EditCustomer.tsx
import React, { useState, useEffect } from "react";
import { Modal, TextInput, Text, Button } from "@mantine/core";
import { RowDataMember } from "../MemberPage/types";
import classes from "./Member.module.css";
import moment from "moment";
import {
  showErrorAlert,
  showWarningAlert,
  showSuccessAlert,
} from "../../helper/alerts";

interface EditCustomerProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveChanges: (editedCustomer: RowDataMember) => void;
  initialData: RowDataMember | null; // Allow initialData to be null
}

const EditCustomer: React.FC<EditCustomerProps> = ({
  isOpen,
  onClose,
  onSaveChanges,
  initialData,
}) => {
  const [editedCustomer, setEditedCustomer] = useState<RowDataMember>({
    name: "",
    email: "",
    nric: "",
    nric_fullname: "",
    contactNo: "",
    profilePicture: "",
    expoPushToken: "",
    referral: "",
    tempCode: "",
    tempCodeTime: "",
    membershipType: "",
    isDealer: "",
    company: "",
    coins: "",
    cash: "",
    daily: "",
    action: "",
    uid: "",
    dob: "",
    isVerified: null,
    isFastPass: null,
    createdAt: "",
    isCorporate: false,
    deleted: false,
  });

  const [dobInput, setDobInput] = useState("");
  const [nricInput, setNricInput] = useState("");

  useEffect(() => {
    if (isOpen && initialData) {
      setEditedCustomer(initialData);
      setDobInput(
        initialData.dob ? moment(initialData.dob).format("YYYY-MM-DD") : ""
      );
      setNricInput(
        initialData.nric || ""
      );
    } else if (!isOpen) {
      setEditedCustomer({
        name: "",
        email: "",
        nric: "",
        nric_fullname: "",
        contactNo: "",
        profilePicture: "",
        expoPushToken: "",
        referral: "",
        tempCode: "",
        tempCodeTime: "",
        membershipType: "",
        isDealer: "",
        company: "",
        coins: "",
        cash: "",
        daily: "",
        action: "",
        uid: "",
        dob: "",
        isVerified: null,
        isFastPass: null,
        createdAt: "",
        isCorporate: false,
        deleted: false,
      });
      setDobInput("");
      setNricInput("");
    }
  }, [isOpen, initialData]);

  const handleSaveChanges = () => {
    try {
      if (editedCustomer.email && editedCustomer.email.trim() !== "") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editedCustomer.email)) {
          showWarningAlert("Please fill in a valid email address");
          return;
        }
      }

      if (nricInput && nricInput.trim() !== "") {
        if (!/^\d+$/.test(nricInput)) {
          showWarningAlert("Please fill in a valid nric");
          return;
        }

        if (nricInput.length < 12) {
          showWarningAlert("Please fill in a valid nric");
          return;
        }
      }

      if (dobInput && dobInput.trim() !== "") {
        if (!moment(dobInput, "YYYY-MM-DD", true).isValid()) {
          showWarningAlert("Please fill in a valid date in YYYY-MM-DD format");
          return;
        }

        if (nricInput.length === 12) {
          const nricYear = nricInput.substr(0, 2);
          const nricMonth = nricInput.substr(2, 2);
          const nricDay = nricInput.substr(4, 2);

          const currentYear = new Date().getFullYear();
          const currentYearTwoDigits = currentYear % 100;
          let fullYear = parseInt(nricYear, 10);
          if (fullYear > currentYearTwoDigits) {
            fullYear += 1900;
          } else {
            fullYear += 2000;
          }
    
          const nricDob = `${fullYear}-${nricMonth}-${nricDay}`;
    
          if (nricDob !== dobInput) {
            showWarningAlert(`DOB does not match IC No.`);
            return;
          }
        }
      }

      editedCustomer.dob = dobInput || "";
      editedCustomer.nric = nricInput || "";

      onSaveChanges(editedCustomer);
      showSuccessAlert("Customer Successfully Updated");
    } catch (error) {
      console.error("Error during validation:", error);
      showErrorAlert("An error occurred during validation. Please try again.");
    }
  };

  return (
    <Modal
      opened={isOpen}
      title={
        <div style={{ textAlign: "center", fontWeight: "bold" }}>
          Edit Customer{" "}
        </div>
      }
      onClose={onClose}
      size="md"
      padding="lg"
    >
      <div className={classes.addCustomerForm}>
        {/* Display fields for editing */}
        <TextInput
          label="Name"
          maxLength={255}
          value={editedCustomer.name}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              name: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Email"
          value={editedCustomer.email}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              email: e.currentTarget.value,
            })
          }
        />
        <TextInput
                label="Contact No."
                value={editedCustomer?.contactNo}
                leftSection={<Text fz={14} c='#2D2D2D' lh={'normal'}>+60</Text>}
                readOnly
              />
        <TextInput
          label="IC No."
          value={nricInput}
          onChange={(e) => {
            setNricInput(e.currentTarget.value);
          }}
        />
        <TextInput
          label="IC Fullname"
          value={editedCustomer.nric_fullname}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              nric_fullname: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="DOB"
          value={dobInput}
          onChange={(e) => {
              setDobInput(e.currentTarget.value);
          }}
          placeholder="YYYY-MM-DD"
        />
        {/*
        <TextInput
          label="Profile Picture  "
          value={editedCustomer.profilePicture}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              profilePicture: e.currentTarget.value,
            })
          }
        />

        <TextInput
          label="expoPushToken"
          value={editedCustomer.expoPushToken}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              expoPushToken: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Temp Code"
          value={editedCustomer.tempCode}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              tempCode: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Temp Code Time"
          value={editedCustomer.tempCodeTime}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              tempCodeTime: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Membership Type"
          value={editedCustomer.membershipType}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              membershipType: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Dealer"
          value={editedCustomer.isDealer}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              isDealer: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Company"
          value={editedCustomer.company}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              company: e.currentTarget.value,
            })
          }
        />
        <TextInput
          label="Daily"
          value={editedCustomer.daily}
          onChange={(e) =>
            setEditedCustomer({
              ...editedCustomer,
              daily: e.currentTarget.value,
            })
          }
        /> */}

        <TextInput label="Referral" value={editedCustomer.referral} readOnly />
        <TextInput label="Coins" value={editedCustomer.coins} readOnly />
        <TextInput label="Cash" value={editedCustomer.cash} readOnly />

        <Button onClick={handleSaveChanges} className={classes.modaleditbutton}>
          Save Changes
        </Button>
      </div>
    </Modal>
  );
};

export default EditCustomer;
